import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PUAN_HAREKETI_ETIKETI, oyunKuralSemasi, type OyunKurali } from "@koclukpanel/contracts";
import { Alan, Buton, Iskelet, Kart, MetinAlani, Sekmeler, type Sekme } from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import {
  ApiHatasi,
  oyunDuzelt,
  oyunKazanimHizi,
  oyunKurallari,
  oyunModulAyarla,
  oyunModulDurumu,
  oyunSupheliKayitlar,
  oyunYeniKural,
  oyunYonetimHareketleri,
  type OyunYonetimHareketi,
} from "./api";
import { CarkAyarlari } from "./CarkAyarlari";
import { KocDavetleri, KocSiparisGecmisi } from "./KocOdulGorunumu";
import { LiderlikGorunumu } from "./LiderlikGorunumu";
import { MagazaYonetimi } from "./MagazaYonetimi";
import { SiparisYonetimi } from "./SiparisYonetimi";
import { useSudoKapisi } from "./SudoKapisi";

/**
 * ÖDÜLLER (yönetim) — F-GAM-03/05/06/10/12/15, oyunlaştırma dalga 1.
 *
 * Modül anahtarı, ortalama kazanım hızı, kural sürümleri (yeni sürüm yalnız ileriye; sudo), puan hareketleri dökümü
 * (elle / tahsil edilemeyen geri alma süzgeçleriyle) ve gerekçeli düzeltme (sudo), şüpheli kayıtlar. Bütün kapsam ve
 * kurallar SUNUCUDA; bu ekran sunucunun cevabını gösterir.
 *
 * Dalga 2 / 7a + 7b: üstte Genel (kurallar ve puan) · Mağaza · Siparişler · Çark · Liderlik görünüm seçici. Mağaza ve Siparişler yalnız
 * ilgili izin varsa görünür (`urunYonetebilir`, `siparisYonetebilir`); hiçbiri yoksa seçici hiç çizilmez ve ekran
 * eskisi gibi tek parça kalır.
 */
type OyunBolumu = "genel" | "magaza" | "siparisler" | "cark" | "liderlik";

/** Başlık bandının gri alt metni — UNİKOÇ ödül ekranlarının cümle kalıbı (tarama-2 §3.5-3.6). */
const BOLUM_ACIKLAMASI: Record<OyunBolumu, string> = {
  genel: "Puan kuralları, kazanım hızı ve hareketleri yönetin.",
  magaza: "Öğrencilerin puanlarla aldığı hediyeleri yönetin.",
  siparisler: "Mağaza siparişlerini takip edin, ödüllerin teslimini yönetin.",
  cark: "Günlük çarkın açık/kapalı durumunu ve dilim tablosunu yönetin.",
  liderlik: "Haftalık liderlik tablosunu sınıf ve haftaya göre inceleyin.",
};

export function OyunYonetimi({
  modulYonetebilir,
  kuralDuzenleyebilir,
  duzeltebilir,
  genelGorebilir = true,
  urunYonetebilir = false,
  siparisYonetebilir = false,
  kagitOnayVerebilir = false,
  carkGorebilir = false,
  carkYonetebilir = false,
  liderlikGorebilir = false,
  hareketGorebilir = false,
  siparisGorebilir = false,
  ilkBolum,
}: {
  modulYonetebilir: boolean;
  kuralDuzenleyebilir: boolean;
  duzeltebilir: boolean;
  /** `oyun.kural.goruntule` — Genel bölümün kartları (kazanım hızı, kurallar, hareketler) bu izinle okunur. */
  genelGorebilir?: boolean;
  urunYonetebilir?: boolean;
  siparisYonetebilir?: boolean;
  kagitOnayVerebilir?: boolean;
  carkGorebilir?: boolean;
  carkYonetebilir?: boolean;
  liderlikGorebilir?: boolean;
  /**
   * Koç Ödül Merkezi (sürüm 9a): `oyun.hareket.goruntule` / `oyun.siparis.goruntule`. Yönetim izni olmayan, yalnız
   * GÖREN rol (koç) için Genel ve Siparişler bölümü SALT OKUR çizilir; kapsam sunucuda.
   */
  hareketGorebilir?: boolean;
  siparisGorebilir?: boolean;
  /** Açılışta seçili bölüm (Ödül Merkezi girdileri: Çark, Mağaza, Sipariş). İzni yoksa ilk izinli bölüm. */
  ilkBolum?: OyunBolumu;
}) {
  // Bölümler GERÇEK izinlerden (Codex 7A-03): OPS_ADMIN `oyun.kural.goruntule` taşımaz ama mağaza ve sipariş
  // izinleri var — Genel'i hiç görmez, açılışta ilk izinli bölüm seçilir.
  const bolumler: Sekme<OyunBolumu>[] = [];
  // Salt okur görünüm: yönetim izni YOKSA, yalnız görme izni olan bölümü okur (koç).
  const genelSaltOkur = !genelGorebilir && hareketGorebilir;
  const siparisSaltOkur = !siparisYonetebilir && siparisGorebilir;
  if (genelGorebilir) bolumler.push({ deger: "genel", etiket: "Genel" });
  else if (genelSaltOkur) bolumler.push({ deger: "genel", etiket: "Puan ve davetler" });
  if (urunYonetebilir) bolumler.push({ deger: "magaza", etiket: "Mağaza" });
  if (siparisYonetebilir) bolumler.push({ deger: "siparisler", etiket: "Siparişler" });
  else if (siparisSaltOkur) bolumler.push({ deger: "siparisler", etiket: "Sipariş geçmişi" });
  if (carkGorebilir) bolumler.push({ deger: "cark", etiket: "Çark" });
  if (liderlikGorebilir) bolumler.push({ deger: "liderlik", etiket: "Liderlik" });
  const [bolum, setBolum] = useState<OyunBolumu | null>(ilkBolum ?? null);
  // İzin sonradan düşerse seçili bölüm kalmasın: görünmeyen bölüm yerine ilk izinli bölüm.
  const etkinBolum: OyunBolumu | null = bolumler.some((b) => b.deger === bolum)
    ? bolum
    : (bolumler[0]?.deger ?? null);
  if (etkinBolum === null) {
    return (
      <p role="status" className="text-notr-600 text-sm">
        Ödül yönetiminde size açık bir bölüm yok.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama={BOLUM_ACIKLAMASI[etkinBolum]} />
      {bolumler.length > 1 && (
        <Sekmeler
          etiket="Ödül yönetimi bölümü"
          sekmeler={bolumler}
          secili={etkinBolum}
          onSec={setBolum}
        />
      )}
      {etkinBolum === "genel" && genelGorebilir && (
        <>
          <ModulKarti yonetebilir={modulYonetebilir} />
          <KazanimHiziKarti />
          <KurallarKarti duzenleyebilir={kuralDuzenleyebilir} />
          <HareketlerKarti duzeltebilir={duzeltebilir} />
          <SupheliKarti />
        </>
      )}
      {etkinBolum === "genel" && genelSaltOkur && (
        <>
          <KocDavetleri />
          <HareketlerKarti duzeltebilir={false} />
        </>
      )}
      {etkinBolum === "magaza" && <MagazaYonetimi />}
      {etkinBolum === "siparisler" && siparisYonetebilir && (
        <SiparisYonetimi kagitOnayVerebilir={kagitOnayVerebilir} />
      )}
      {etkinBolum === "siparisler" && !siparisYonetebilir && <KocSiparisGecmisi />}
      {etkinBolum === "cark" && <CarkAyarlari yonetebilir={carkYonetebilir} />}
      {etkinBolum === "liderlik" && <LiderlikGorunumu />}
    </div>
  );
}

function hataYazisi(e: unknown, varsayilan: string): string {
  return e instanceof ApiHatasi ? e.message : varsayilan;
}

function ModulKarti({ yonetebilir }: { yonetebilir: boolean }) {
  const qc = useQueryClient();
  const durum = useQuery({ queryKey: ["oyun-modul"], queryFn: oyunModulDurumu, retry: false });
  const mut = useMutation({
    mutationFn: (acik: boolean) => oyunModulAyarla(acik),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["oyun-modul"] }),
  });
  return (
    <Kart baslik="Modül" aciklama="Kapalıyken öğrenci ödül ekranı ve puan işleri durur.">
      {durum.isPending ? (
        <Iskelet satir={1} />
      ) : durum.error ? (
        <p role="alert" className="text-hata-700 text-sm">
          Modül durumu alınamadı.
        </p>
      ) : (
        <div className="flex items-center gap-3 text-sm">
          <span className="text-notr-900 font-medium">{durum.data.acik ? "Açık" : "Kapalı"}</span>
          {yonetebilir && (
            <Buton
              gorunum="ikincil"
              disabled={mut.isPending}
              onClick={() => mut.mutate(!durum.data.acik)}
            >
              {durum.data.acik ? "Kapat" : "Aç"}
            </Buton>
          )}
        </div>
      )}
    </Kart>
  );
}

function KazanimHiziKarti() {
  const q = useQuery({ queryKey: ["oyun-kazanim-hizi"], queryFn: oyunKazanimHizi, retry: false });
  return (
    <Kart
      baslik="Ortalama kazanım"
      aciklama="Kesinleşmiş son haftalarda öğrenci başına haftalık çaba puanı (elle, teşekkür ve düzeltme sayılmaz)."
    >
      {q.isPending ? (
        <Iskelet satir={1} />
      ) : q.error ? (
        <p role="alert" className="text-hata-700 text-sm">
          Kazanım hızı alınamadı.
        </p>
      ) : q.data.ogrenciHafta === 0 ? (
        <p className="text-notr-600 text-sm">Henüz kesinleşmiş hafta yok.</p>
      ) : (
        <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          <Deger etiket="Medyan / hafta" deger={q.data.medyan} />
          <Deger etiket="Üst çeyrek / hafta" deger={q.data.ustCeyrek} />
          <Deger etiket="Ortalama / hafta" deger={q.data.ortalama} />
          <Deger etiket={`Öğrenci-hafta (son ${q.data.hafta})`} deger={q.data.ogrenciHafta} />
        </dl>
      )}
    </Kart>
  );
}

function Deger({ etiket, deger }: { etiket: string; deger: number }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-notr-600">{etiket}</dt>
      <dd className="text-notr-900 text-lg font-semibold tabular-nums">{deger}</dd>
    </div>
  );
}

/** Düzenlenebilir sayısal alanlar — kural JSON'unun yolu ve ekrandaki adı. */
const KURAL_ALANLARI: { yol: string; ad: string }[] = [
  { yol: "gorev.tam", ad: "Görev tam puan" },
  { yol: "gorev.az", ad: "Görev kısmi puan" },
  { yol: "gorev.gecTam", ad: "Geç giriş tam" },
  { yol: "gorev.gecAz", ad: "Geç giriş kısmi" },
  { yol: "gorev.gunTavani", ad: "Günlük tavan" },
  { yol: "gorev.hedefsizTamSoru", ad: "Hedefsiz görev tam (soru)" },
  { yol: "gorev.tamSureDk", ad: "Hedefsiz görev tam (dk)" },
  { yol: "gorev.azSureDk", ad: "Hedefsiz görev kısmi (dk)" },
  { yol: "hafta.esikler.0.puan", ad: "Hafta %100 puanı" },
  { yol: "hafta.esikler.1.puan", ad: "Hafta %80 puanı" },
  { yol: "hafta.haftaAsgariGorev", ad: "Haftalık asgari görev" },
  { yol: "deneme.puan", ad: "Deneme puanı" },
  { yol: "koc.hareketTavani", ad: "Koç tek seferde en çok" },
  { yol: "koc.haftalikOgrenciTavani", ad: "Koç öğrenci-hafta tavanı" },
  { yol: "kampanya.instagram", ad: "Instagram teşekkürü" },
  { yol: "supheli.hedefKati", ad: "Şüpheli: hedefin katı" },
  { yol: "supheli.soruBasinaAsgariSn", ad: "Şüpheli: soru başına sn" },
  { yol: "supheli.gorevAzamiSaat", ad: "Şüpheli: görevde saat" },
  { yol: "sure.hareketsizAy", ad: "Hareketsizlik (ay)" },
  { yol: "sure.bildirimGunOnce", ad: "Bildirim (gün önce)" },
];

function oku(k: unknown, yol: string): number {
  return yol.split(".").reduce<unknown>((o, p) => (o as Record<string, unknown>)?.[p], k) as number;
}
function yaz(k: OyunKurali, yol: string, deger: number): OyunKurali {
  const kopya = JSON.parse(JSON.stringify(k)) as Record<string, unknown>;
  const p = yol.split(".");
  let o = kopya as Record<string, unknown>;
  for (const parca of p.slice(0, -1)) o = o[parca] as Record<string, unknown>;
  o[p[p.length - 1]!] = deger;
  return kopya as unknown as OyunKurali;
}

function sonrakiPazartesi(): string {
  const d = new Date();
  const g = d.getDay();
  d.setDate(d.getDate() + (g === 0 ? 1 : 8 - g));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function KurallarKarti({ duzenleyebilir }: { duzenleyebilir: boolean }) {
  const q = useQuery({ queryKey: ["oyun-kurallar"], queryFn: oyunKurallari, retry: false });
  const [form, setForm] = useState(false);
  const son = q.data?.at(-1);
  return (
    <Kart
      baslik="Puan kuralları"
      aciklama="Kural değişmez; yeni sürüm açılır ve yalnız ileriye (en erken sonraki Pazartesi) geçerlidir."
    >
      {q.isPending ? (
        <Iskelet satir={3} />
      ) : q.error ? (
        <p role="alert" className="text-hata-700 text-sm">
          Kurallar alınamadı.
        </p>
      ) : (
        <div className="flex flex-col gap-2 text-sm">
          <ul className="divide-notr-100 divide-y">
            {q.data.map((s) => (
              <li key={s.surum} className="py-1">
                <span className="text-notr-900 font-medium">Sürüm {s.surum}</span>{" "}
                <span className="text-notr-600">
                  — {s.gecerliBaslangic} haftasından · {s.gerekce}
                </span>
              </li>
            ))}
          </ul>
          {duzenleyebilir && son && !form && (
            <div>
              <Buton gorunum="ikincil" onClick={() => setForm(true)}>
                Yeni sürüm
              </Buton>
            </div>
          )}
          {form && son && <YeniSurumFormu taban={son.kurallar} onKapat={() => setForm(false)} />}
        </div>
      )}
    </Kart>
  );
}

function YeniSurumFormu({ taban, onKapat }: { taban: OyunKurali; onKapat: () => void }) {
  const qc = useQueryClient();
  const [kural, setKural] = useState<OyunKurali>(taban);
  const [baslangic, setBaslangic] = useState(sonrakiPazartesi());
  const [gerekce, setGerekce] = useState("");
  const { hataYakala, kapi } = useSudoKapisi(
    "Puan kuralını değiştirmek için şifrenizi yeniden girin.",
  );
  const mut = useMutation({
    mutationFn: () =>
      oyunYeniKural({ kurallar: kural, gecerliBaslangic: baslangic, gerekce: gerekce.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["oyun-kurallar"] });
      onKapat();
    },
    onError: hataYakala(() => mut.mutate()),
  });
  const dogrulama = oyunKuralSemasi.safeParse(kural);
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (dogrulama.success) mut.mutate();
      }}
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {KURAL_ALANLARI.map((a) => (
          <Alan
            key={a.yol}
            etiket={a.ad}
            type="number"
            min={0}
            value={String(oku(kural, a.yol))}
            onChange={(e) => setKural(yaz(kural, a.yol, Number(e.target.value)))}
          />
        ))}
      </div>
      <Alan
        etiket="Geçerli olduğu Pazartesi"
        type="date"
        value={baslangic}
        onChange={(e) => setBaslangic(e.target.value)}
      />
      <MetinAlani
        etiket="Gerekçe"
        value={gerekce}
        onChange={(e) => setGerekce(e.target.value)}
        rows={2}
        required
      />
      {!dogrulama.success && (
        <p role="alert" className="text-hata-700 text-xs">
          {dogrulama.error.issues[0]?.message ?? "Kural geçersiz."}
        </p>
      )}
      {mut.isError && !(mut.error instanceof ApiHatasi && mut.error.sudoGerekliMi) && (
        <p role="alert" className="text-hata-700 text-xs">
          {hataYazisi(mut.error, "Sürüm açılamadı.")}
        </p>
      )}
      <div className="flex gap-2">
        <Buton
          type="submit"
          disabled={!dogrulama.success || gerekce.trim().length < 10 || mut.isPending}
        >
          Sürümü aç
        </Buton>
        <Buton type="button" gorunum="ikincil" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
      {kapi}
    </form>
  );
}

function HareketlerKarti({ duzeltebilir }: { duzeltebilir: boolean }) {
  const [suzgec, setSuzgec] = useState<"hepsi" | "elle" | "kesilen">("hepsi");
  const [duzeltilen, setDuzeltilen] = useState<OyunYonetimHareketi | null>(null);
  const q = useQuery({
    queryKey: ["oyun-hareketler", suzgec],
    queryFn: () =>
      oyunYonetimHareketleri({ elle: suzgec === "elle", kesilen: suzgec === "kesilen" }),
    retry: false,
  });
  return (
    <Kart
      baslik="Puan hareketleri"
      aciklama="Son hareketler. Tahsil edilemeyen geri alma: bakiyede durdurulan eksi fark."
    >
      <div role="group" aria-label="Hareket süzgeci" className="mb-2 flex flex-wrap gap-2">
        {(
          [
            ["hepsi", "Hepsi"],
            ["elle", "Elle verilen / düzeltme"],
            ["kesilen", "Tahsil edilemeyen geri alma"],
          ] as const
        ).map(([k, ad]) => (
          <Buton
            key={k}
            gorunum={suzgec === k ? undefined : "ikincil"}
            aria-pressed={suzgec === k}
            onClick={() => setSuzgec(k)}
          >
            {ad}
          </Buton>
        ))}
      </div>
      {q.isPending ? (
        <Iskelet satir={4} />
      ) : q.error ? (
        <p role="alert" className="text-hata-700 text-sm">
          Hareketler alınamadı.
        </p>
      ) : q.data.satirlar.length === 0 ? (
        <p className="text-notr-600 text-sm">Kayıt yok.</p>
      ) : (
        <ul className="divide-notr-100 divide-y text-sm">
          {q.data.satirlar.map((h) => (
            <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div>
                <p className="text-notr-900">
                  {h.ogrenciAdi} · {PUAN_HAREKETI_ETIKETI[h.tur] ?? h.tur}
                </p>
                <p className="text-notr-600 text-xs">
                  {h.tarih.slice(0, 10)}
                  {h.miktar !== h.istenenMiktar
                    ? ` · istenen ${h.istenenMiktar}, bakiyede ${h.miktar}`
                    : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold tabular-nums">
                  {h.miktar > 0 ? `+${h.miktar}` : h.miktar}
                </span>
                {duzeltebilir && (
                  <Buton gorunum="sessiz" onClick={() => setDuzeltilen(h)}>
                    Düzelt
                  </Buton>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {duzeltilen && <DuzeltmeFormu hareket={duzeltilen} onKapat={() => setDuzeltilen(null)} />}
    </Kart>
  );
}

function DuzeltmeFormu({
  hareket,
  onKapat,
}: {
  hareket: OyunYonetimHareketi;
  onKapat: () => void;
}) {
  const qc = useQueryClient();
  const [istekKimligi] = useState(() => crypto.randomUUID());
  const [miktar, setMiktar] = useState("");
  const [gerekce, setGerekce] = useState("");
  const { hataYakala, kapi } = useSudoKapisi("Puan düzeltmek için şifrenizi yeniden girin.");
  const mut = useMutation({
    mutationFn: () =>
      oyunDuzelt(hareket.ogrenciId, {
        miktar: Number(miktar),
        gerekce: gerekce.trim(),
        istekKimligi,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["oyun-hareketler"] });
      onKapat();
    },
    onError: hataYakala(() => mut.mutate()),
  });
  const n = Number(miktar);
  return (
    <form
      className="border-notr-200 mt-3 flex flex-col gap-2 border-t pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        mut.mutate();
      }}
    >
      <p className="text-notr-900 text-sm font-medium">{hareket.ogrenciAdi} — gerekçeli düzeltme</p>
      <Alan
        etiket="Miktar (eksi için -)"
        type="number"
        value={miktar}
        onChange={(e) => setMiktar(e.target.value)}
        zorunlu
      />
      <MetinAlani
        etiket="Gerekçe"
        value={gerekce}
        onChange={(e) => setGerekce(e.target.value)}
        rows={2}
        required
      />
      {mut.isError && !(mut.error instanceof ApiHatasi && mut.error.sudoGerekliMi) && (
        <p role="alert" className="text-hata-700 text-xs">
          {hataYazisi(mut.error, "Düzeltme yapılamadı.")}
        </p>
      )}
      <div className="flex gap-2">
        <Buton
          type="submit"
          disabled={!Number.isInteger(n) || n === 0 || gerekce.trim().length < 10 || mut.isPending}
        >
          Düzelt
        </Buton>
        <Buton type="button" gorunum="ikincil" onClick={onKapat}>
          Vazgeç
        </Buton>
      </div>
      {kapi}
    </form>
  );
}

function SupheliKarti() {
  const q = useQuery({ queryKey: ["oyun-supheli"], queryFn: oyunSupheliKayitlar, retry: false });
  const NEDEN: Record<string, string> = {
    HEDEFIN_KATI: "hedefin çok üstünde çözülen",
    TOPLAM_HEDEFIN_KATI: "hedefin çok üstünde toplam",
    SORU_BASINA_SURE: "soru başına süre çok kısa",
    SURE_ASIRI: "görevde süre çok uzun",
  };
  return (
    <Kart
      baslik="Şüpheli kayıtlar"
      aciklama="Son 4 hafta. Puan almadılar; koç sonucu düzeltir ya da yeniden kaydederse puan doğar."
    >
      {q.isPending ? (
        <Iskelet satir={2} />
      ) : q.error ? (
        <p role="alert" className="text-hata-700 text-sm">
          Şüpheli kayıtlar alınamadı.
        </p>
      ) : q.data.length === 0 ? (
        <p className="text-notr-600 text-sm">Şüpheli kayıt yok.</p>
      ) : (
        <ul className="divide-notr-100 divide-y text-sm">
          {q.data.map((s) => (
            <li key={s.gorevId} className="py-1">
              <span className="text-notr-900">{s.ogrenciAdi}</span>{" "}
              <span className="text-notr-600">
                · {s.gun} · {s.nedenler.map((n) => NEDEN[n] ?? n).join(", ")}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Kart>
  );
}
